"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_ATTACK_DELAY_TICKS,
  LegacyGame,
  POSE_CELLS,
  RULES_HASH,
  canonicalStringify,
  createEmptyBoard,
  generateLegacyCues,
  getPoseCells,
  serializeSnapshot,
  sha256,
  type GameEvent,
  type InputControl,
  type LockEvent,
  type PieceId,
} from "../../lib/game";
import {
  DEFAULT_CHECKPOINT_INTERVAL_TICKS,
  LOGICAL_TICK_RATE,
  PROTOCOL_VERSION,
  PeerMatchSession,
  RematchAgreement,
  roomWebSocketUrl,
  type LockCommitMessage,
  type MatchEnvelope,
  type RematchMessage,
  type ResultAckMessage,
  type StartAgreement,
  type TopOutMessage,
} from "../../lib/net";
import { decodeUtf8, encodeUtf8, expandBoard, flattenBoard, packInput, unpackInput } from "../match-codec";
import {
  applyInputToActiveGame,
  isVerifiedRemoteTopOut,
  quantizeInputTransitionTick,
} from "../battle-boundaries";
import { recordLossAndMaybeShowInterstitial } from "../admob";
import { useLegacyAudio, useLegacyAudioSettings } from "../hooks/useLegacyAudio";
import { loadKeyBindings, saveKeyBindings, useLegacyKeyboard, type KeyBindings } from "../hooks/useLegacyKeyboard";
import { useTouchControlsSettings } from "../hooks/useTouchControlsSettings";
import { normalizePlayerProfile } from "../profile";
import { BattleArena, type BoardView } from "./BattleArena";
import { CpuMode } from "./CpuMode";
import { HomeMenu, type AppMode } from "./HomeMenu";
import { KeyConfigPanel } from "./KeyConfigPanel";
import { MatchmakingMode } from "./MatchmakingMode";
import { MatchOverlay } from "./MatchOverlay";
import { PracticeMode } from "./PracticeMode";
import { RoomMode } from "./RoomMode";
import type { LegacyAction } from "./TouchControls";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

type Phase =
  | "lobby"
  | "connecting"
  | "waiting"
  | "countdown"
  | "playing"
  | "paused"
  | "finish"
  | "result";

type RoundResult = "win" | "loss" | "draw" | "no-contest";

type TopOutRecord = {
  hash: string;
  lockSeq: number;
  senderId: string;
  tick: number;
};

type QueuedInput = {
  applyBeforeTick: number;
  control: InputControl;
  held: boolean;
  sequence: number;
};

const ROOM_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const MATCH_MANIFEST_HASH = sha256(
  canonicalStringify({
    attackDelayTicks: DEFAULT_ATTACK_DELAY_TICKS,
    checkpointTicks: DEFAULT_CHECKPOINT_INTERVAL_TICKS,
    protocolVersion: PROTOCOL_VERSION,
    rulesHash: RULES_HASH,
    tickRate: LOGICAL_TICK_RATE,
    version: "bwwk-p2p-fidelity-v1",
  }),
);

const ACTION_TO_INPUT: Readonly<Record<LegacyAction, InputControl>> = {
  ccw: "rotateCCW",
  cw: "rotateCW",
  left: "left",
  right: "right",
  softDrop: "softDrop",
  hardDrop: "hardDrop",
};

function randomRoomCode(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return Array.from(bytes, (byte) => ROOM_ALPHABET[byte & 31]).join("");
}

function initialRoomCode(): string {
  if (typeof window === "undefined") return "BATTLE24";
  const value = new URL(window.location.href).searchParams.get("room") ?? "";
  const normalized = value.toUpperCase().replace(/[^ABCDEFGHJKLMNPQRSTUVWXYZ23456789]/gu, "");
  return normalized.length >= 6 ? normalized.slice(0, 12) : randomRoomCode();
}

function initialAppMode(): AppMode | "home" {
  if (typeof window !== "undefined" && new URL(window.location.href).searchParams.has("room")) {
    return "custom";
  }
  return "home";
}

const BACKGROUND_STORAGE_KEY = "bwwk-background-index-v2";

function initialBackgroundIndex(): number {
  if (typeof window === "undefined") return 0;
  const value = Number(window.localStorage.getItem(BACKGROUND_STORAGE_KEY) ?? "0");
  return Number.isSafeInteger(value) && value >= 0 ? value % 5 : 0;
}

function signalingOrigin(): string {
  const viteEnvironment = (import.meta as ImportMeta & {
    env?: Record<string, string | undefined>;
  }).env;
  const configured = viteEnvironment?.VITE_SIGNALING_ORIGIN?.trim();
  if (configured) return new URL(configured).origin;
  if (typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname)) {
    return "http://127.0.0.1:8787";
  }
  throw new Error("公開版のシグナリングURLが設定されていません。VITE_SIGNALING_ORIGIN を設定してください。");
}

function senderId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function nextCells(piece: number): ReadonlyArray<readonly [number, number]> {
  if (piece < 1 || piece > 7) return [];
  return POSE_CELLS[piece as PieceId][0].map(({ dx, dy }) => [dx, dy] as const);
}

function viewFromGame(game: LegacyGame, received: number): BoardView {
  const active = game.active;
  return {
    active: getPoseCells(active).map(({ x, y }) => ({ x, y, piece: active.piece })),
    blockIndex: game.blockIndex,
    board: game.board,
    nextCells: nextCells(game.nextPiece),
    nextPiece: game.nextPiece,
    pending: game.pendingGarbageLines,
    received,
    topOut: game.topOut,
  };
}

function emptyView(): BoardView {
  return {
    active: [],
    board: createEmptyBoard(),
    nextCells: [],
    nextPiece: 0,
    pending: 0,
    received: 0,
    topOut: false,
  };
}

function resultLabel(result: RoundResult): string {
  switch (result) {
    case "win": return "YOU WIN";
    case "loss": return "YOU LOSE";
    case "draw": return "DRAW";
    case "no-contest": return "NO CONTEST";
  }
}

function invertRemoteResult(result: ResultAckMessage["result"]): RoundResult {
  if (result === "win") return "loss";
  if (result === "loss") return "win";
  return result;
}

export function BattleClient() {
  const [room] = useState(initialRoomCode);
  const [playerName, setPlayerName] = useState(() => {
    if (typeof window === "undefined") return "";
    const profile = window.localStorage.getItem("bwwk-player-profile");
    if (profile !== null) return normalizePlayerProfile(profile);
    const name = window.localStorage.getItem("bwwk-player-name") ?? "";
    const trip = window.localStorage.getItem("bwwk-player-trip") ?? "";
    return normalizePlayerProfile(trip && !name.includes("#") ? `${name}#${trip}` : name);
  });
  const [playerId, setPlayerId] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem("bwwk-player-id") ?? "";
  });
  const [appMode, setAppMode] = useState<AppMode | "home">(initialAppMode);
  const [phase, setPhase] = useState<Phase>("lobby");
  const [status, setStatus] = useState("同じルーム番号を2台で入力してください");
  const [seat, setSeat] = useState<1 | 2>(1);
  const [quality, setQuality] = useState("P2P WAITING");
  const [overlay, setOverlay] = useState<"fight" | "finish" | null>(null);
  const [localView, setLocalView] = useState<BoardView>(emptyView);
  const [remoteView, setRemoteView] = useState<BoardView>(emptyView);
  const [result, setResult] = useState<RoundResult | null>(null);
  const [resultDetail, setResultDetail] = useState("");
  const [round, setRound] = useState(1);
  const [rematchWaiting, setRematchWaiting] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [keyBindings, setKeyBindings] = useState<KeyBindings>(loadKeyBindings);
  const [keyConfigOpen, setKeyConfigOpen] = useState(false);
  const [roomSettingsOpen, setRoomSettingsOpen] = useState(false);
  const [backgroundIndex, setBackgroundIndex] = useState(initialBackgroundIndex);
  const { settings: audioSettings, updateSettings: updateAudioSettings } = useLegacyAudioSettings();
  const { settings: touchControlsSettings, updateSettings: updateTouchControlsSettings } = useTouchControlsSettings();

  const cycleBackground = useCallback(() => {
    setBackgroundIndex((current) => {
      const next = (current + 1) % 5;
      try {
        window.localStorage.setItem(BACKGROUND_STORAGE_KEY, String(next));
      } catch {
        // The current background still changes for this session.
      }
      return next;
    });
  }, []);

  const openSettingsFromRoom = useCallback(() => {
    setRoomSettingsOpen(true);
  }, []);

  const phaseRef = useRef<Phase>("lobby");
  const gameRef = useRef<LegacyGame | undefined>(undefined);
  const remoteGameRef = useRef<LegacyGame | undefined>(undefined);
  const sessionRef = useRef<PeerMatchSession | undefined>(undefined);
  const seedRef = useRef<string | undefined>(undefined);
  const cueRef = useRef<readonly PieceId[]>([]);
  const inputSequenceRef = useRef(0);
  const inputOrderRef = useRef(0);
  const queuedInputsRef = useRef<QueuedInput[]>([]);
  const requestedInputsRef = useRef<Record<InputControl, boolean>>({
    left: false,
    right: false,
    rotateCCW: false,
    rotateCW: false,
    softDrop: false,
    hardDrop: false,
  });
  const checkpointTickRef = useRef(0);
  const expectedRemoteLocksRef = useRef(new Map<number, LockEvent>());
  const remoteCommitsRef = useRef(new Map<number, LockCommitMessage>());
  const localTopOutRef = useRef<TopOutRecord | undefined>(undefined);
  const remoteTopOutRef = useRef<TopOutRecord | undefined>(undefined);
  const resultRef = useRef<RoundResult | undefined>(undefined);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const generationRef = useRef(0);
  const roundRef = useRef(1);
  const roomRef = useRef(room);
  const localReceivedRef = useRef(0);
  const remoteReceivedRef = useRef(0);
  const remotePendingRef = useRef(0);
  const lateAttackRef = useRef(0);
  const rematchRef = useRef<RematchAgreement | undefined>(undefined);
  const initialSeatRef = useRef<1 | 2 | undefined>(undefined);
  const processEventsRef = useRef<(events: readonly GameEvent[]) => void>(() => undefined);
  const advanceGameRef = useRef<(tick: number) => void>(() => undefined);
  const settleResultRef = useRef<() => void>(() => undefined);
  const { playLock, unlock } = useLegacyAudio();

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  }, []);

  const relayOnly = useMemo(
    () => typeof window !== "undefined" && new URL(window.location.href).searchParams.get("relay") === "1",
    [],
  );

  function setMatchPhase(next: Phase): void {
    phaseRef.current = next;
    setPhase(next);
  }

  const handleKeyBindingsChange = useCallback((next: KeyBindings) => {
    setKeyBindings(next);
    saveKeyBindings(next);
  }, []);

  const handlePlayerNameChange = useCallback((value: string) => {
    const normalized = normalizePlayerProfile(value);
    setPlayerName(normalized);
    try {
      window.localStorage.setItem("bwwk-player-profile", normalized);
      const separator = normalized.indexOf("#");
      window.localStorage.setItem("bwwk-player-name", (separator < 0 ? normalized : normalized.slice(0, separator)).trim());
      window.localStorage.setItem("bwwk-player-trip", separator < 0 ? "" : normalized.slice(separator + 1).trim());
    } catch {
      // Private browsing can reject localStorage; the current session still uses the value.
    }
  }, []);

  const handlePlayerIdChange = useCallback((value: string) => {
    setPlayerId(value);
    try {
      window.localStorage.setItem("bwwk-player-id", value);
    } catch {
      // Private browsing can reject localStorage; the current session still uses the value.
    }
  }, []);

  function resetRoundState(): void {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = undefined;
    gameRef.current = undefined;
    remoteGameRef.current = undefined;
    seedRef.current = undefined;
    cueRef.current = [];
    inputSequenceRef.current = 0;
    inputOrderRef.current = 0;
    queuedInputsRef.current = [];
    requestedInputsRef.current = {
      left: false,
      right: false,
      rotateCCW: false,
      rotateCW: false,
      softDrop: false,
      hardDrop: false,
    };
    checkpointTickRef.current = 0;
    expectedRemoteLocksRef.current.clear();
    remoteCommitsRef.current.clear();
    localTopOutRef.current = undefined;
    remoteTopOutRef.current = undefined;
    resultRef.current = undefined;
    localReceivedRef.current = 0;
    remoteReceivedRef.current = 0;
    remotePendingRef.current = 0;
    lateAttackRef.current = 0;
    rematchRef.current = undefined;
    setResult(null);
    setResultDetail("");
    setRematchWaiting(false);
    setOverlay(null);
    setLocalView(emptyView());
    setRemoteView(emptyView());
  }

  function advanceRemoteVerifier(targetTick: number): void {
    const verifier = remoteGameRef.current;
    if (!verifier || targetTick < verifier.tick) return;
    for (const event of verifier.stepTo(targetTick)) {
      if (event.type === "lock") expectedRemoteLocksRef.current.set(event.lockSeq, event);
    }
  }

  function finishRound(
    nextResult: RoundResult,
    detail: string,
    acknowledgedTopOut?: TopOutRecord,
  ): void {
    if (resultRef.current) return;
    resultRef.current = nextResult;
    if (nextResult === "loss") recordLossAndMaybeShowInterstitial();
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = undefined;
    setResult(nextResult);
    setResultDetail(detail);
    setStatus("FINISH");
    setMatchPhase("finish");
    setOverlay("finish");

    if (acknowledgedTopOut) {
      try {
        sessionRef.current?.sendResultAck({
          accepted: nextResult !== "no-contest",
          finalStateHash: acknowledgedTopOut.hash,
          reason: nextResult === "no-contest" ? detail : undefined,
          result: nextResult,
          topOutSenderId: acknowledgedTopOut.senderId,
          topOutTick: acknowledgedTopOut.tick,
        });
      } catch {
        // The local result still renders when the peer disappears after top-out.
      }
    }
  }

  function noContest(detail: string): void {
    finishRound("no-contest", detail);
  }

  function settleTopOuts(): void {
    if (resultRef.current) return;
    const local = localTopOutRef.current;
    const remote = remoteTopOutRef.current;

    if (local && remote) {
      if (local.tick === remote.tick) {
        finishRound("draw", `同一tick (${local.tick}) で両者TOP OUT`, remote);
      } else if (remote.tick < local.tick) {
        finishRound("win", `相手が tick ${remote.tick} でTOP OUT`, remote);
      } else {
        finishRound("loss", `tick ${local.tick} でTOP OUT`);
      }
      return;
    }

    if (remote && !local) {
      const game = gameRef.current;
      if (game && game.tick > remote.tick) {
        finishRound("win", `相手が tick ${remote.tick} でTOP OUT`, remote);
      }
    }
  }

  settleResultRef.current = settleTopOuts;

  function processLocalEvents(events: readonly GameEvent[]): void {
    const session = sessionRef.current;
    const game = gameRef.current;
    if (!session || !game) return;

    for (const event of events) {
      if (event.type === "pose") {
        try {
          session.sendPose({
            pieceIndex: event.blockIndex,
            poseTick: event.tick,
            rotation: event.rotation,
            x: event.x,
            y: event.y,
          });
        } catch {
          // Pose is deliberately lossy; the next Lock Commit remains authoritative.
        }
        continue;
      }

      if (event.type === "lock") {
        playLock(event.clearedLines);
        localReceivedRef.current += event.garbageApplied;
        try {
          session.sendLockCommit({
            attackLines: event.attackLines,
            blockIndex: event.blockIndex,
            board: flattenBoard(event.board),
            boardHash: event.boardHash,
            clearedLines: event.clearedLines,
            garbageApplied: event.garbageApplied,
            holeIndex: event.holeIndex,
            lockSeq: event.lockSeq,
            lockTick: event.tick,
            nextPiece: event.nextPiece,
          });

          if (event.attackLines > 0) {
            session.sendAttack({
              createdTick: event.tick,
              lines: event.attackLines,
              sourceLockSeq: event.lockSeq,
            });
            const attackId = `${session.senderId}:${event.lockSeq}`;
            remoteGameRef.current?.applyScheduledAttack({
              attackId,
              createdTick: event.tick,
              eligibleTick: event.tick + DEFAULT_ATTACK_DELAY_TICKS,
              lines: event.attackLines,
              sourceLockSeq: event.lockSeq,
            });
            remotePendingRef.current += event.attackLines;
          }
          session.takeEligibleAttacksAtLock(event.tick);
        } catch (error) {
          setStatus(error instanceof Error ? error.message : "確定盤面を送信できませんでした");
        }
      } else if (event.type === "topout") {
        const record: TopOutRecord = {
          hash: event.stateHash,
          lockSeq: event.lockSeq,
          senderId: session.senderId,
          tick: event.tick,
        };
        localTopOutRef.current = record;
        try {
          session.sendTopOut({
            finalStateHash: event.stateHash,
            lastLockSeq: event.lockSeq,
            topOutTick: event.tick,
          });
        } catch {
          // A disconnected loser still reaches a local FINISH state below.
        }
        settleTopOuts();
        if (!resultRef.current) {
          settleTimerRef.current = setTimeout(() => {
            if (!resultRef.current && localTopOutRef.current && !remoteTopOutRef.current) {
              finishRound("loss", `tick ${event.tick} でTOP OUT`);
            }
          }, 1_000);
        }
      }
    }
  }

  processEventsRef.current = processLocalEvents;

  function advanceLocalGame(targetTick: number): void {
    const game = gameRef.current;
    const session = sessionRef.current;
    if (!game || !session || targetTick < game.tick) return;

    while (queuedInputsRef.current[0]?.applyBeforeTick <= targetTick && !game.topOut) {
      const queued = queuedInputsRef.current.shift();
      if (!queued) break;
      const tickBeforeSample = Math.max(game.tick, queued.applyBeforeTick - 1);
      if (tickBeforeSample > game.tick) processLocalEvents(game.stepTo(tickBeforeSample));
      if (game.topOut) {
        queuedInputsRef.current = [];
        break;
      }
      const inputSequence = inputSequenceRef.current + 1;
      if (!applyInputToActiveGame(game, queued.control, queued.held, inputSequence)) {
        queuedInputsRef.current = [];
        break;
      }
      inputSequenceRef.current = inputSequence;
      try {
        session.sendInputBatch({
          firstTick: game.tick,
          inputSeq: inputSequence,
          packedInputs: packInput(queued.control, queued.held),
        });
      } catch (error) {
        setStatus(error instanceof Error ? error.message : "入力を送信できませんでした");
      }
    }

    if (!game.topOut && targetTick > game.tick) processLocalEvents(game.stepTo(targetTick));
  }

  advanceGameRef.current = advanceLocalGame;

  function validateRemoteCommit(message: LockCommitMessage): void {
    advanceRemoteVerifier(message.lockTick);
    const expected = expectedRemoteLocksRef.current.get(message.lockSeq);
    if (!expected) {
      noContest(`相手の Lock Commit ${message.lockSeq} を入力履歴から再現できませんでした`);
      return;
    }
    expectedRemoteLocksRef.current.delete(message.lockSeq);
    if (
      expected.tick !== message.lockTick ||
      expected.boardHash !== message.boardHash ||
      expected.clearedLines !== message.clearedLines ||
      expected.attackLines !== message.attackLines ||
      expected.garbageApplied !== message.garbageApplied
    ) {
      noContest(`相手の確定盤面ハッシュが一致しません (lock ${message.lockSeq})`);
    }
  }

  function handleRemoteTopOut(
    message: TopOutMessage,
    envelope: MatchEnvelope<TopOutMessage>,
  ): void {
    advanceRemoteVerifier(message.topOutTick);
    const verifier = remoteGameRef.current;
    if (!isVerifiedRemoteTopOut(verifier, message)) {
      noContest("相手のTOP OUTを確定入力から検証できませんでした");
      return;
    }
    remoteTopOutRef.current = {
      hash: message.finalStateHash,
      lockSeq: message.lastLockSeq,
      senderId: envelope.senderId,
      tick: message.topOutTick,
    };
    settleTopOuts();
  }

  function handleRemoteResult(message: ResultAckMessage): void {
    if (resultRef.current) return;
    const localResult = invertRemoteResult(message.result);
    finishRound(
      localResult,
      message.reason ?? (localResult === "loss" ? "自分のTOP OUTが確認されました" : "勝敗が確認されました"),
    );
  }

  function handleRematchMessage(message: RematchMessage): void {
    const agreement = currentRematchAgreement();
    const transition = agreement.receive(message);
    if (transition.ignored) return;
    if (transition.declined) {
      setRematchWaiting(false);
      setResultDetail("相手が再戦を辞退しました");
      return;
    }
    if (message.action === "request" && !agreement.localRequested) {
      setResultDetail("相手が再戦を希望しています");
    }
    if (transition.sendAccept) {
      try {
        sessionRef.current?.respondToRematch(
          "accept",
          agreement.round,
          agreement.proposedMatchId,
        );
      } catch (error) {
        setRematchWaiting(false);
        setResultDetail(error instanceof Error ? error.message : "再戦合意を送信できませんでした");
        return;
      }
    }
    if (transition.ready && agreement.beginRestart()) restartRound(agreement.round);
  }

  function roundMatchId(roundNumber: number): string {
    return `bwwk:${roomRef.current}:round:${roundNumber}`;
  }

  function currentRematchAgreement(): RematchAgreement {
    const nextRound = roundRef.current + 1;
    const nextMatchId = roundMatchId(nextRound);
    const current = rematchRef.current;
    if (
      current &&
      current.round === nextRound &&
      current.proposedMatchId === nextMatchId
    ) {
      return current;
    }
    const agreement = new RematchAgreement(nextRound, nextMatchId);
    rematchRef.current = agreement;
    return agreement;
  }

  function restartRound(nextRound: number): void {
    const oldSession = sessionRef.current;
    generationRef.current += 1;
    setRematchWaiting(true);
    setResultDetail("再戦の入室枠を切り替えています…");
    void (async () => {
      let resumeToken: string;
      try {
        const reservation = await oldSession?.closeAndWait("rematch");
        if (!reservation) throw new Error("再戦用の席予約を取得できませんでした");
        if (
          initialSeatRef.current !== undefined &&
          reservation.seat !== initialSeatRef.current
        ) {
          throw new Error("再戦用の席番号が初回と一致しません");
        }
        resumeToken = reservation.resumeToken;
      } catch (error) {
        if (sessionRef.current === oldSession) sessionRef.current = undefined;
        setRematchWaiting(false);
        setResultDetail(
          error instanceof Error
            ? `再戦の退出確認に失敗しました: ${error.message}`
            : "再戦の退出確認に失敗しました",
        );
        return;
      }
      if (sessionRef.current === oldSession) sessionRef.current = undefined;
      roundRef.current = nextRound;
      setRound(nextRound);
      resetRoundState();
      setMatchPhase("connecting");
      setStatus("再戦へ接続しています…");
      await startSession(nextRound, resumeToken);
    })();
  }

  async function startSession(
    roundNumber: number,
    resumeToken?: string,
    retryAttempt = 0,
  ): Promise<void> {
    const generation = ++generationRef.current;
    resetRoundState();
    setMatchPhase("connecting");
    setStatus("部屋へ接続しています…");

    let session: PeerMatchSession | undefined;
    try {
      const origin = signalingOrigin();
      const matchRoom = roomRef.current;
      session = new PeerMatchSession({
        manifestHash: MATCH_MANIFEST_HASH,
        matchId: roundMatchId(roundNumber),
        relayOnly,
        rulesHash: RULES_HASH,
        senderId: senderId(),
        signaling: {
          websocketUrl: roomWebSocketUrl(origin, matchRoom),
          ...(resumeToken ? { resumeToken } : {}),
        },
        tickProvider: () => sessionRef.current?.gameClock?.nowTick() ?? gameRef.current?.tick ?? 0,
        turnCredentialOrigin: origin,
        callbacks: {
          onAttack: (scheduled, envelope) => {
            if (generation !== generationRef.current || resultRef.current) return;
            const commit = remoteCommitsRef.current.get(scheduled.message.sourceLockSeq);
            if (
              !commit ||
              commit.lockTick !== scheduled.message.createdTick ||
              commit.attackLines !== scheduled.message.lines ||
              scheduled.message.eligibleTick !== scheduled.message.createdTick + DEFAULT_ATTACK_DELAY_TICKS
            ) {
              noContest("攻撃データが直前の Lock Commit と一致しません");
              return;
            }
            if (scheduled.late) {
              lateAttackRef.current += 1;
              setQuality(`LATE ATTACK ${lateAttackRef.current}`);
              if (lateAttackRef.current >= 2) {
                noContest("攻撃が固定境界を越えて複数回遅着しました");
                return;
              }
            }
            gameRef.current?.applyScheduledAttack({
              attackId: scheduled.message.attackId,
              createdTick: scheduled.message.createdTick,
              eligibleTick: scheduled.message.eligibleTick,
              lines: scheduled.message.lines,
              sourceLockSeq: scheduled.message.sourceLockSeq,
            });
            setLocalView((view) => ({
              ...view,
              pending: gameRef.current?.pendingGarbageLines ?? view.pending,
            }));
            remoteCommitsRef.current.delete(envelope.message.sourceLockSeq);
          },
          onChannelsOpen: () => {
            if (generation !== generationRef.current) return;
            setStatus("接続完了 — 開始時刻を合わせています");
          },
          onCheckpoint: (message, envelope) => {
            if (generation !== generationRef.current || resultRef.current) return;
            advanceRemoteVerifier(message.checkpointTick);
            const verifier = remoteGameRef.current;
            if (!verifier) return;
            if (verifier.stateHash() === message.stateHash) {
              sessionRef.current?.confirmCheckpoint(envelope.senderId, message.stateHash);
            } else {
              try {
                const transmitted = JSON.parse(decodeUtf8(message.stateBytes)) as { tick?: unknown };
                if (transmitted.tick === message.checkpointTick) {
                  noContest(`checkpoint ${message.checkpointTick} の状態が一致しません`);
                }
              } catch {
                noContest("相手のcheckpointを復号できませんでした");
              }
            }
          },
          onClockEstimate: (estimate) => {
            if (generation !== generationRef.current) return;
            const relay = relayOnly ? " / RELAY" : "";
            setQuality(`RTT ${Math.round(estimate.medianRoundTripMs)}ms${relay}`);
          },
          onError: (error) => {
            if (generation !== generationRef.current) return;
            setStatus(error.message);
            if (["playing", "paused"].includes(phaseRef.current)) setQuality("CONNECTION ERROR");
          },
          onInputBatch: (message) => {
            if (generation !== generationRef.current || resultRef.current) return;
            try {
              const verifier = remoteGameRef.current;
              if (!verifier || message.firstTick < verifier.tick) {
                noContest("相手の入力履歴が時系列順ではありません");
                return;
              }
              advanceRemoteVerifier(message.firstTick);
              const input = unpackInput(message.packedInputs);
              verifier.setInput(input.control, input.held, message.inputSeq);
            } catch (error) {
              noContest(error instanceof Error ? error.message : "相手の入力を検証できませんでした");
            }
          },
          onLockCommit: (message) => {
            if (generation !== generationRef.current || resultRef.current) return;
            try {
              validateRemoteCommit(message);
              // The original battle sprite plays the same settle/clear cue for
              // settleRemote as it does for the local lock.  Lock Commit is
              // authoritative, so this also keeps the remote sound in sync
              // without depending on packet arrival timing or Pose updates.
              playLock(message.clearedLines);
              if (message.attackLines > 0) remoteCommitsRef.current.set(message.lockSeq, message);
              else remoteCommitsRef.current.delete(message.lockSeq);
              remoteReceivedRef.current += message.garbageApplied;
              remotePendingRef.current = Math.max(0, remotePendingRef.current - message.garbageApplied);
              const verifier = remoteGameRef.current;
              const activePiece = verifier?.active;
            setRemoteView({
                blockIndex: verifier?.blockIndex,
                active: activePiece
                  ? getPoseCells(activePiece).map(({ x, y }) => ({ x, y, piece: activePiece.piece }))
                  : [],
                board: expandBoard(message.board),
                nextCells: nextCells(message.nextPiece),
                nextPiece: message.nextPiece,
                pending: remotePendingRef.current,
                received: remoteReceivedRef.current,
                topOut: verifier?.topOut ?? false,
              });
            } catch (error) {
              noContest(error instanceof Error ? error.message : "相手の盤面確定を検証できませんでした");
            }
          },
          onPeerConnectionState: (connectionState) => {
            if (generation !== generationRef.current) return;
            if (["disconnected", "failed"].includes(connectionState) && phaseRef.current === "playing") {
              setMatchPhase("paused");
              setQuality("RECONNECTING");
              setStatus("接続が途切れました。再接続を待っています…");
            }
          },
          onPeerJoined: () => {
            if (generation !== generationRef.current) return;
            setStatus("接続完了 — P2Pを確立しています");
          },
          onPeerLeft: (message) => {
            if (generation !== generationRef.current) return;
            if (message.final && ["playing", "paused"].includes(phaseRef.current)) {
              noContest("相手が部屋を退出しました");
            } else {
              setStatus("相手の再接続を15秒待っています…");
              setQuality("RECONNECTING");
            }
          },
          onPose: (message) => {
            if (generation !== generationRef.current || resultRef.current) return;
            const piece = cueRef.current[message.pieceIndex % 500];
            if (!piece) return;
            const active = { piece, rotation: message.rotation as 0 | 1 | 2 | 3, x: message.x, y: message.y };
            setRemoteView((view) => ({
              ...view,
              blockIndex: message.pieceIndex,
              active: getPoseCells(active).map(({ x, y }) => ({ x, y, piece })),
            }));
          },
          onRematch: (message) => {
            if (generation !== generationRef.current) return;
            handleRematchMessage(message);
          },
          onResultAck: (message) => {
            if (generation !== generationRef.current) return;
            handleRemoteResult(message);
          },
          onSeedReady: (seed) => {
            if (generation !== generationRef.current) return;
            seedRef.current = seed;
            cueRef.current = generateLegacyCues(seed).pieceCue;
          },
          onSignalingJoined: (joined) => {
            if (generation !== generationRef.current) return;
            if (resumeToken && !joined.resumed) {
              throw new Error("再戦用の席予約を再開できませんでした");
            }
            if (initialSeatRef.current === undefined) initialSeatRef.current = joined.seat;
            if (joined.seat !== initialSeatRef.current) {
              throw new Error("再戦時の自席を維持できませんでした");
            }
            setSeat(initialSeatRef.current);
            setMatchPhase("waiting");
            setStatus(joined.peers.length ? "接続完了 — P2Pを確立しています" : "接続完了 — 相手を待っています");
            const url = new URL(window.location.href);
            url.searchParams.set("room", joined.roomCode);
            window.history.replaceState(null, "", url);
          },
          onStartAgreed: (agreement: StartAgreement) => {
            if (generation !== generationRef.current) return;
            const seed = seedRef.current;
            if (!seed) {
              noContest("共通seedの合意前に開始時刻が確定しました");
              return;
            }
            gameRef.current = new LegacyGame({
              matchId: roundMatchId(roundNumber),
              seed,
              startTick: agreement.proposal.startTick,
            });
            remoteGameRef.current = new LegacyGame({
              matchId: roundMatchId(roundNumber),
              seed,
              startTick: agreement.proposal.startTick,
            });
            checkpointTickRef.current = agreement.proposal.startTick;
            setLocalView(viewFromGame(gameRef.current, 0));
            setRemoteView(viewFromGame(remoteGameRef.current, 0));
            setMatchPhase("countdown");
            setStatus("接続完了");
          },
          onStateChange: (next) => {
            if (generation !== generationRef.current) return;
            if (next === "playing") {
              setMatchPhase("playing");
              setOverlay("fight");
              setStatus("対戦中");
              const pose = gameRef.current?.currentPoseEvent();
              if (pose) {
                try {
                  sessionRef.current?.sendPose({
                    pieceIndex: pose.blockIndex,
                    poseTick: pose.tick,
                    rotation: pose.rotation,
                    x: pose.x,
                    y: pose.y,
                  }, true);
                } catch {
                  // A first Pose is optional; cadence resumes at tick 18.
                }
              }
            } else if (next === "reconnecting" && phaseRef.current === "playing") {
              setMatchPhase("paused");
              setQuality("RECONNECTING");
            }
          },
          onTopOut: (message, envelope) => {
            if (generation !== generationRef.current) return;
            handleRemoteTopOut(message, envelope);
          },
        },
      });
      sessionRef.current = session;
      await session.connect();
    } catch (error) {
      if (generation !== generationRef.current) return;
      if (resumeToken && session?.seat === undefined && retryAttempt < 3) {
        session?.close();
        if (sessionRef.current === session) sessionRef.current = undefined;
        setStatus(`再戦の席を再確認しています… (${retryAttempt + 1}/3)`);
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 250 * 2 ** retryAttempt);
        });
        return startSession(roundNumber, resumeToken, retryAttempt + 1);
      }
      setMatchPhase("lobby");
      setStatus(error instanceof Error ? error.message : "接続できませんでした");
    }
  }

  const handleInput = useCallback((action: LegacyAction, held: boolean) => {
    void unlock();
    if (phaseRef.current !== "playing" || resultRef.current) return;
    const game = gameRef.current;
    const session = sessionRef.current;
    if (!game || !session) return;

    const control = ACTION_TO_INPUT[action];
    if (requestedInputsRef.current[control] === held) return;
    requestedInputsRef.current[control] = held;

    const applyBeforeTick = quantizeInputTransitionTick(
      game.startTick,
      game.tick,
      control,
      queuedInputsRef.current,
    );
    queuedInputsRef.current.push({
      applyBeforeTick,
      control,
      held,
      sequence: ++inputOrderRef.current,
    });
    queuedInputsRef.current.sort(
      (left, right) => left.applyBeforeTick - right.applyBeforeTick || left.sequence - right.sequence,
    );
  }, [unlock]);

  useLegacyKeyboard(phase === "playing", handleInput, "A", keyBindings);

  useEffect(() => {
    let frame = 0;
    const render = () => {
      const game = gameRef.current;
      const session = sessionRef.current;
      if (phaseRef.current === "playing" && game && session?.gameClock && !resultRef.current) {
        const tick = session.gameClock.nowTick();
        if (tick > game.tick) advanceGameRef.current(tick);
        setLocalView(viewFromGame(game, localReceivedRef.current));

        if (game.tick - checkpointTickRef.current >= DEFAULT_CHECKPOINT_INTERVAL_TICKS) {
          checkpointTickRef.current = game.tick;
          try {
            const snapshot = game.snapshot();
            session.sendCheckpoint({
              checkpointTick: game.tick,
              lastInputSeq: game.lastInputSeq,
              lastLockSeq: game.lastLockSeq,
              stateBytes: encodeUtf8(serializeSnapshot(snapshot)),
              stateHash: game.stateHash(),
            });
          } catch {
            // A later checkpoint will retry; gameplay never waits for the network.
          }
        }
        settleResultRef.current();
      }
      frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => () => {
    generationRef.current += 1;
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    sessionRef.current?.close();
  }, []);

  const handleBackHome = () => {
    if (phase !== "lobby") return;
    setRoomSettingsOpen(false);
    setAppMode("home");
    setKeyConfigOpen(false);
    setStatus("モードを選択してください");
  };

  const handleSelectMode = (nextMode: AppMode) => {
    if (phase !== "lobby") return;
    setRoomSettingsOpen(false);
    setKeyConfigOpen(false);
    setAppMode(nextMode);
    if (nextMode === "custom") setStatus("同じROOM IDを2台で入力してください");
  };

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") setInstallPrompt(null);
  };

  const handleRematch = () => {
    if (!resultRef.current) return;
    const agreement = currentRematchAgreement();
    if (agreement.localRequested) return;
    const transition = agreement.requestLocal();
    setRematchWaiting(true);
    setResultDetail(
      agreement.remoteRequested
        ? "再戦合意を確認しています…"
        : "相手の再戦操作を待っています…",
    );
    try {
      sessionRef.current?.requestRematch(agreement.round, agreement.proposedMatchId);
      if (transition.sendAccept) {
        sessionRef.current?.respondToRematch(
          "accept",
          agreement.round,
          agreement.proposedMatchId,
        );
      }
      if (transition.ready && agreement.beginRestart()) restartRound(agreement.round);
    } catch (error) {
      setRematchWaiting(false);
      setResultDetail(error instanceof Error ? error.message : "再戦を要求できませんでした");
    }
  };

  const p1 = seat === 1 ? localView : remoteView;
  const p2 = seat === 2 ? localView : remoteView;
  const showArena = ["countdown", "playing", "paused", "finish", "result"].includes(phase);

  return (
    <main className={`game-root background-theme-${backgroundIndex}${showArena ? " is-arena" : ""}`}>
      {!showArena ? (
        <>
          {appMode === "home" ? (
            keyConfigOpen ? (
              <KeyConfigPanel
                audioSettings={audioSettings}
                bindings={keyBindings}
                profileId={playerId}
                profileName={playerName}
                touchControlsSettings={touchControlsSettings}
                onBack={() => setKeyConfigOpen(false)}
                onAudioSettingsChange={updateAudioSettings}
                onProfileIdChange={handlePlayerIdChange}
                onProfileNameChange={handlePlayerNameChange}
                onTouchControlsSettingsChange={updateTouchControlsSettings}
                onChange={handleKeyBindingsChange}
              />
            ) : (
              <HomeMenu
                installable={Boolean(installPrompt)}
                onInstall={() => void handleInstall()}
                onOpenKeyConfig={() => setKeyConfigOpen(true)}
                onSelect={handleSelectMode}
              />
            )
          ) : null}
          {appMode === "practice" ? <PracticeMode keyBindings={keyBindings} onBack={handleBackHome} /> : null}
          {appMode === "rooms" || appMode === "custom" || appMode === "casual" ? (
            <RoomMode
              casualMode={appMode === "casual"}
              customMode={appMode === "custom"}
              initialPlayerName={playerName}
              initialPlayerId={playerId}
              initialRoom={room}
              keyBindings={keyBindings}
              onBack={handleBackHome}
              onCycleBackground={cycleBackground}
              onOpenSettings={openSettingsFromRoom}
              settingsOpen={roomSettingsOpen}
              onPlayerNameChange={handlePlayerNameChange}
              onPlayerIdChange={handlePlayerIdChange}
              backgroundIndex={backgroundIndex}
            />
          ) : null}
          {roomSettingsOpen && (appMode === "rooms" || appMode === "custom" || appMode === "casual") ? (
            <KeyConfigPanel
              audioSettings={audioSettings}
              bindings={keyBindings}
              profileId={playerId}
              profileName={playerName}
              touchControlsSettings={touchControlsSettings}
              onBack={() => setRoomSettingsOpen(false)}
              onAudioSettingsChange={updateAudioSettings}
              onProfileIdChange={handlePlayerIdChange}
              onProfileNameChange={handlePlayerNameChange}
              onTouchControlsSettingsChange={updateTouchControlsSettings}
              onChange={handleKeyBindingsChange}
            />
          ) : null}
          {appMode === "cpu" ? (
            <CpuMode keyBindings={keyBindings} onBack={handleBackHome} />
          ) : null}
          {appMode === "ranked" ? (
            <MatchmakingMode
              mode={appMode}
              onBack={handleBackHome}
              onOpenCasual={() => setAppMode("casual")}
            />
          ) : null}
        </>
      ) : (
        <>
          <BattleArena
            controlsEnabled={phase === "playing"}
            localSeat={seat}
            onInput={handleInput}
            p1={p1}
            p2={p2}
            quality={quality}
          />
          <div className="connection-ribbon">
            ROOM {room} / ROUND {round} / {status}
          </div>
          <MatchOverlay
            kind={overlay}
            onComplete={() => {
              if (overlay === "finish") setMatchPhase("result");
              setOverlay(null);
            }}
          />
          {phase === "result" && result ? (
            <div className="result-panel">
              <section className="result-card">
                <h2>{resultLabel(result)}</h2>
                <p>{resultDetail}</p>
                <button className="chrome-button" disabled={rematchWaiting} onClick={handleRematch} type="button">
                  {rematchWaiting ? "再戦待ち…" : "再戦"}
                </button>
              </section>
            </div>
          ) : null}
        </>
      )}

      {showArena ? (
        <div className="portrait-guard" role="status">
          <div>
            <span aria-hidden="true" className="portrait-phone" />
            <strong>端末を横向きにしてください</strong>
            <span>原作と同じ比率で2つの盤面を表示します</span>
          </div>
        </div>
      ) : null}
    </main>
  );
}
